import React from "react";
import PropTypes from "prop-types";
import { NumericTextBoxComponent } from "@syncfusion/ej2-react-inputs";
import classNames from "classnames";

/**
 * NumericInput — Syncfusion NumericTextBoxComponent wrapper with project-standard styling.
 *
 * Replaces react-number-format `NumericFormat` with a native Syncfusion numeric
 * input. Supports currency prefix, thousand separators, and custom formats.
 *
 * @example
 * <NumericInput placeholder="Loan Amount *" value={field.value} onChange={field.onChange} format="c0" currency="USD" />
 *
 * @example
 * <NumericInput placeholder="FICO Score" value={field.value} onChange={field.onChange} min={300} max={850} format="n0" />
 */
export default function NumericInput({
  placeholder,
  value,
  onChange,
  error,
  min,
  max,
  step = 1,
  decimals = 0,
  format = "n0",
  currency,
  disabled = false,
  className,
  floatLabelType = "Auto",
  ...props
}) {
  return (
    <div className={classNames("numeric-input-wrapper", className)}>
      <NumericTextBoxComponent
        placeholder={placeholder}
        floatLabelType={floatLabelType}
        value={value ?? null}
        change={e => onChange?.(e.value)}
        min={min}
        max={max}
        step={step}
        decimals={decimals}
        format={format}
        currency={currency}
        validateDecimalOnType
        enabled={!disabled}
        cssClass={classNames("e-outline", { "e-error": !!error })}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

NumericInput.propTypes = {
  placeholder: PropTypes.string,
  value: PropTypes.number,
  onChange: PropTypes.func,
  error: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  decimals: PropTypes.number,
  /** Syncfusion number format string — "n0", "c0", "c2", etc. */
  format: PropTypes.string,
  /** ISO currency code — "USD", "CAD", etc. Used when format starts with "c" */
  currency: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  floatLabelType: PropTypes.oneOf(["Auto", "Always", "Never"]),
};

